import { useState, useCallback } from 'react';

export interface UseStarRatingReturn {
    selectedStars: number;
    hoveredStar: number;
    isDragging: boolean;
    setSelectedStars: (stars: number) => void;
    setHoveredStar: (star: number) => void;
    setIsDragging: (dragging: boolean) => void;
    getStarFromPosition: (clientX: number, containerElement: HTMLElement, starsCount: number) => number;
    handleTouchStart: () => void;
    handleTouchMove: (e: React.TouchEvent, starsCount: number) => void;
    handleTouchEnd: () => void;
    handleStarClick: (starNumber: number) => (e: React.MouseEvent) => void;
    handlePointerEnter: (starNumber: number) => (e: React.PointerEvent) => void;
    handlePointerLeave: () => (e: React.PointerEvent) => void;
}

export const useStarRating = (initialRating: number = 0): UseStarRatingReturn => {
    const [selectedStars, setSelectedStars] = useState<number>(initialRating);
    const [hoveredStar, setHoveredStar] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const getStarFromPosition = useCallback((clientX: number, containerElement: HTMLElement, starsCount: number): number => {
        const rect = containerElement.getBoundingClientRect();
        const relativeX = clientX - rect.left;
        const starWidth = rect.width / starsCount;
        const starIndex = Math.ceil(relativeX / starWidth);
        return Math.max(1, Math.min(starsCount, starIndex));
    }, []);

    const handleTouchStart = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent, starsCount: number) => {
        if (!isDragging) return;
        e.preventDefault();
        const star = getStarFromPosition(e.touches[0].clientX, e.currentTarget as HTMLElement, starsCount);
        setSelectedStars(star);
        setHoveredStar(star);
    }, [isDragging, getStarFromPosition]);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
        setHoveredStar(0);
    }, []);

    const handleStarClick = useCallback((starNumber: number) => (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isDragging) {
            setSelectedStars(starNumber);
        }
    }, [isDragging]);

    const handlePointerEnter = useCallback((starNumber: number) => (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') {
            setHoveredStar(starNumber);
        }
    }, []);

    const handlePointerLeave = useCallback(() => (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') {
            setHoveredStar(0);
        }
    }, []);

    return {
        selectedStars,
        hoveredStar,
        isDragging,
        setSelectedStars,
        setHoveredStar,
        setIsDragging,
        getStarFromPosition,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleStarClick,
        handlePointerEnter,
        handlePointerLeave,
    };
};
